import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useStudents } from '@/app/redux/hooks/students';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import {
  ContentPanelBody,
  ContentPanelCloseButton,
  ContentPanelCollapseButton,
  ContentPanelContainer,
  ContentPanelControls,
  ContentPanelHeader,
  ContentPanelLeft,
  ContentPanelRight,
  ContentPanelTabsTrigger,
  ContentPanelTitle,
} from '@/common/ui/ContentPanel';
import { FeatureSwitch, useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import ShareLinkDialogue from '@/common/ui/modals/ShareLinkDialogue';
import SearchInput from '@/common/ui/SearchInput';

import { ProIcon } from '@/common/ui/UpgradeButton';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import CreateAssessmentDialogue from '@/dashboard/Assessment/CreateAssessmentDialogue';

import { useGroups } from '@/app/redux/hooks/groups';
import { NotFoundError } from '@/common/utils/error';
import InsightsFeatureGate from '@/dashboard/Insights/InsightsFeatureGate';
import LearnerAssessments from '@/dashboard/LearnerView/LearnerAssessments';
import LearnerInsights from '@/dashboard/LearnerView/LearnerInsights';
import LearnerNotes, {
  type ViewMode,
} from '@/dashboard/LearnerView/LearnerNotes';
import {
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChartLineIcon,
  GearIcon,
  LinkIcon,
  ListIcon,
  NotebookIcon,
  PencilLineIcon,
  PlusIcon,
  PulseIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { Tabs, TabsContent, TabsList } from '@shadcn/components/tabs';
import { ToggleGroup, ToggleGroupItem } from '@shadcn/components/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import LevelProgressIndicator from '../LevelProgressionIndicator';
import ActivityFeed from './ActivityFeed/ActivityFeed';
import LearnerDetails from './LearnerDetails';

export default function IndividualLearnerView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groupId, tab: activeTab, studentId } = useParams();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { students, studentsById, isLoading, isFetching } = useStudents();
  const stateStudent = studentId ? studentsById.get(studentId) : undefined;

  const { shareLink, student: shareLinkStudent } =
    useAppSelector(selectShareLink);
  const isShareLink = !!shareLink;
  const student = shareLinkStudent ?? stateStudent;

  const { groupsById } = useGroups();
  const group = groupId ? groupsById.get(groupId) : undefined;

  const [createAssessmentOpen, setCreateAssessmentOpen] = useState(false);

  const [shareLinkDialogueOpen, setShareLinkDialogueOpen] = useState(false);

  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(true);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [notesViewMode, setNotesViewMode] = useState<ViewMode>(
    getFromLocalStorage('learner-notes-view-mode') || 'gallery',
  );

  useEffect(() => {
    saveToLocalStorage('learner-notes-view-mode', notesViewMode);
  }, [notesViewMode]);

  const studentIds = group ? group.studentIds : students.map((s) => s.id);
  const selectedStudentIdx = student ? studentIds.indexOf(student.id) : 0;

  function handleNavigateBack() {
    if (groupId) {
      navigate(`/dashboard/groups/${groupId}`);
    } else {
      navigate('/dashboard/learners');
    }
  }

  function handleNavigatePrevious() {
    if (selectedStudentIdx === 0) {
      return;
    }
    navigate(
      groupId
        ? `/dashboard/groups/${groupId}/learners/${
            studentIds[selectedStudentIdx - 1]
          }`
        : `/dashboard/learners/${studentIds[selectedStudentIdx - 1]}`,
    );
  }

  function handleNavigateNext() {
    if (selectedStudentIdx === studentIds.length - 1) {
      return;
    }
    navigate(
      groupId
        ? `/dashboard/groups/${groupId}/learners/${
            studentIds[selectedStudentIdx + 1]
          }`
        : `/dashboard/learners/${studentIds[selectedStudentIdx + 1]}`,
    );
  }

  if (isLoading || isFetching) {
    return (
      <div className="h-svh w-full flex items-center justify-center">
        <DataLoader />
      </div>
    );
  }

  if (!student) {
    throw new NotFoundError('Learner not found');
  }

  const handleCreateNote = () => {
    navigate(`/dashboard/learners/${student.id}/notes/new`);
  };

  const levelDetails = scopeAndSequence.levels[
    student.level - 1
  ] as ScopeAndSequenceLevel;

  return (
    <ContentPanelContainer $fullHeight={isShareLink}>
      <ContentPanelHeader>
        <div className="flex items-center gap-6 align-text-bottom">
          <ContentPanelTitle>
            <InitialsCircle
              userName={`${student.studentFirstName} ${student.studentSurname}`}
              size={24}
            />
            {student.studentFirstName} {student.studentSurname}{' '}
            <span className="text-sm text-muted-foreground">
              {student.spellcastUsername}
            </span>
          </ContentPanelTitle>
          <Separator orientation="vertical" className="h-6" />
          <span>
            Level {student.level} - {levelDetails.description}
          </span>
          <LevelProgressIndicator
            currentLevelProgress={student.levelsProgress[student.level - 1]}
          />
          {!isShareLink && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="px-1 py-1 h-fit"
                  variant="outline"
                  onClick={() => setShareLinkDialogueOpen(true)}
                >
                  <LinkIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Share learner</TooltipContent>
            </Tooltip>
          )}
        </div>

        {isShareLink ? (
          <img
            src="/ui/new-spellcaster-logo.svg"
            className="absolute top-1/2 -translate-y-1/2 right-4 h-10 cursor-pointer"
            alt="Spellcaster"
            onClick={() => navigate('/')}
          />
        ) : (
          <ContentPanelControls>
            <div className="flex gap-1">
              <Button
                className="px-1 py-1 h-fit"
                variant="outline"
                onClick={handleNavigatePrevious}
                disabled={selectedStudentIdx === 0}
              >
                <CaretUpIcon />
              </Button>
              <Button
                className="px-1 py-1 h-fit"
                variant="outline"
                onClick={handleNavigateNext}
                disabled={selectedStudentIdx === studentIds.length - 1}
              >
                <CaretDownIcon />
              </Button>
            </div>

            <span className="text-muted-foreground pointer-events-none">
              {selectedStudentIdx + 1} of {studentIds.length} in{' '}
              {group ? group.name : 'All Students'}
            </span>

            <ContentPanelCloseButton onClick={handleNavigateBack} />
          </ContentPanelControls>
        )}
      </ContentPanelHeader>

      <ContentPanelBody>
        <ContentPanelLeft
          className={isRightPanelCollapsed ? 'flex-1' : undefined}
        >
          <Tabs
            value={activeTab ?? 'activity'}
            className="h-full content-panel-child"
            onValueChange={(value) => {
              let next;
              if (shareLink) {
                next = `/share/${shareLink.type}/${shareLink.id}/${value}`;
              } else {
                const learnerPart = `learners/${student.id}/${value}`;
                next = groupId
                  ? `/dashboard/groups/${groupId}/${learnerPart}`
                  : `/dashboard/${learnerPart}`;
              }
              navigate(next);
            }}
          >
            <LearnerViewContentHeader>
              <TabsList className="bg-white gap-1">
                <ContentPanelTabsTrigger value="activity">
                  <PulseIcon size={16} /> Activity
                </ContentPanelTabsTrigger>
                <ContentPanelTabsTrigger value="assessments">
                  <PencilLineIcon size={16} />
                  Assessments
                  {!isFeatureEnabled('assessments') && <ProIcon />}
                </ContentPanelTabsTrigger>
                <ContentPanelTabsTrigger value="insights">
                  <ChartLineIcon size={16} /> Insights
                  {!isFeatureEnabled('insights') && <ProIcon />}
                </ContentPanelTabsTrigger>
                {!isShareLink && (
                  <FeatureSwitch
                    feature="learner-notes"
                    enabled={
                      <ContentPanelTabsTrigger value="notes">
                        <NotebookIcon size={16} /> Notes
                      </ContentPanelTabsTrigger>
                    }
                    disabled={
                      <ContentPanelTabsTrigger
                        value="notes"
                        onClick={(e) => {
                          e.preventDefault();
                          dispatch(setAttemptedProFeature('Learner Notes'));
                        }}
                      >
                        <NotebookIcon size={16} /> Notes
                        <ProIcon />
                      </ContentPanelTabsTrigger>
                    }
                  />
                )}
              </TabsList>

              {!isShareLink && activeTab === 'assessments' && (
                <div
                  className={cn(
                    'flex items-center gap-2 transition-all duration-200',
                    isRightPanelCollapsed && 'mr-[124px]',
                  )}
                >
                  <FeatureSwitch
                    feature="assessments"
                    enabled={
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* wrap button in span so tooltip doesn't get disabled */}
                          <span>
                            <Button
                              variant="outline"
                              onClick={() => {
                                trackMixpanelEvent(
                                  'Create Assessment clicked from Assessments View',
                                );
                                setCreateAssessmentOpen(true);
                              }}
                              className="gap-2"
                              disabled={!!student.assessmentId}
                            >
                              <PencilLineIcon size={16} /> Create Assessment
                              {student.assessmentId && (
                                <Info color="hsl(var(--primary))" size={16} />
                              )}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {student.assessmentId && (
                          <TooltipContent>
                            {student.studentFirstName} {student.studentSurname}{' '}
                            already has an active assessment
                          </TooltipContent>
                        )}
                      </Tooltip>
                    }
                    disabled={
                      <Button
                        variant="outline"
                        onClick={() => {
                          dispatch(setAttemptedProFeature('Assessments'));
                        }}
                        className="gap-2"
                      >
                        <PencilLineIcon size={16} /> Create Assessment
                        <ProIcon />
                      </Button>
                    }
                  />
                </div>
              )}

              {!isShareLink && activeTab === 'notes' && (
                <div
                  className={cn(
                    'flex items-center gap-2 transition-all duration-200',
                    isRightPanelCollapsed && 'mr-[124px]',
                  )}
                >
                  <ToggleGroup
                    type="single"
                    value={notesViewMode}
                    onValueChange={(value: ViewMode) =>
                      value && setNotesViewMode(value)
                    }
                    className="border rounded-md"
                  >
                    <ToggleGroupItem
                      value="gallery"
                      aria-label="Gallery view"
                      className="px-2"
                    >
                      <SquaresFourIcon className="size-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="list"
                      aria-label="List view"
                      className="px-2"
                    >
                      <ListIcon className="size-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                  <SearchInput
                    placeholder="Search notes..."
                    value={notesSearchTerm}
                    onChange={setNotesSearchTerm}
                  />
                  <FeatureSwitch
                    feature="learner-notes"
                    enabled={
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={handleCreateNote}
                      >
                        <PlusIcon size={16} /> Create Note
                      </Button>
                    }
                    disabled={
                      <Button
                        variant="outline"
                        onClick={() => {
                          dispatch(setAttemptedProFeature('Learner Notes'));
                        }}
                        className="gap-2"
                      >
                        <PlusIcon size={16} /> Create Note
                        <ProIcon />
                      </Button>
                    }
                  />
                </div>
              )}
            </LearnerViewContentHeader>

            <LearnerViewContentBody className="scrollable">
              <TabsContent value="activity">
                <ActivityFeed
                  studentId={student.id}
                  studentName={`${student.studentFirstName} ${student.studentSurname}`}
                />
              </TabsContent>
              <TabsContent value="assessments">
                <LearnerAssessments
                  studentId={student.id}
                  organisationId={student.organisationId}
                  onCreateAssessment={() => setCreateAssessmentOpen(true)}
                />
              </TabsContent>
              <TabsContent value="insights">
                <FeatureSwitch
                  feature="insights"
                  enabled={<LearnerInsights studentId={student.id} />}
                  disabled={<InsightsFeatureGate />}
                />
              </TabsContent>
              {!isShareLink && (
                <TabsContent value="notes">
                  <LearnerNotes
                    studentId={student.id}
                    organisationId={student.organisationId}
                    searchTerm={notesSearchTerm}
                    viewMode={notesViewMode}
                    onCreateNote={handleCreateNote}
                  />
                </TabsContent>
              )}
            </LearnerViewContentBody>
          </Tabs>
        </ContentPanelLeft>

        {!isShareLink && (
          <ContentPanelRight
            className={isRightPanelCollapsed ? 'collapsed' : undefined}
          >
            <Tabs
              value={isRightPanelCollapsed ? '' : 'details'}
              defaultValue="details"
              className="content-panel-child"
            >
              <LearnerViewContentHeader>
                <TabsList className="bg-white gap-1">
                  <ContentPanelCollapseButton
                    onClick={() =>
                      setIsRightPanelCollapsed(!isRightPanelCollapsed)
                    }
                    $isCollapsed={isRightPanelCollapsed}
                  >
                    <ContentPanelTabsTrigger
                      value="details"
                      className="gap-2 bottom-[4px]"
                    >
                      {!isRightPanelCollapsed && <CaretDoubleLeftIcon />}
                      <GearIcon size={18} /> Settings
                      {isRightPanelCollapsed && <CaretDoubleRightIcon />}
                    </ContentPanelTabsTrigger>
                  </ContentPanelCollapseButton>
                </TabsList>
              </LearnerViewContentHeader>

              <LearnerDetailsBody>
                <TabsContent value="details">
                  <LearnerDetails student={student} />
                </TabsContent>
              </LearnerDetailsBody>
            </Tabs>
          </ContentPanelRight>
        )}
      </ContentPanelBody>

      <CreateAssessmentDialogue
        isOpen={createAssessmentOpen}
        onClose={() => setCreateAssessmentOpen(false)}
        initialStudents={[student.id]}
      />

      <ShareLinkDialogue
        open={shareLinkDialogueOpen}
        onClose={() => setShareLinkDialogueOpen(false)}
        studentId={student.id}
        type="student"
        resourceId={student.id}
      />
    </ContentPanelContainer>
  );
}

const LearnerViewContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid hsl(var(--border));
  padding: 4px 16px;
  flex-shrink: 0;
`;

const LearnerViewContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const LearnerDetailsBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;
